/**
 * The ITSM webhook form as the case-management service serves it
 * (`GET /itsm/schema`, captured 2026-10-13) — static, no tenant data. Demo
 * mode answers with this, parsed through the same schema as live.
 */
export const DEMO_ITSM_SCHEMA: unknown = {
  "plugin_name": "itsm_webhook",
  "display_name": "ITSM Webhook",
  "version": "1.0",
  "allow_multi": false,
  "providers": [
    {
      "value": "jira",
      "label": "Jira",
      "description": "Jira Software / Jira Service Management. Default for configs without a provider.",
      "defaults": {}
    },
    {
      "value": "manage_engine",
      "label": "ManageEngine",
      "description": "ManageEngine Service Desk (form-encoded webhooks).",
      "defaults": {
        "webhook_body_format": "form",
        "webhook_form_param": "input_data"
      }
    },
    {
      "value": "helix",
      "label": "BMC Helix",
      "description": "BMC Helix ITSM (JWT two-step login).",
      "defaults": {
        "webhook_auth_type": "HelixJWT"
      }
    },
    {
      "value": "sapphire",
      "label": "SapphireIMS",
      "description": "SapphireIMS (fully config-driven).",
      "defaults": {}
    },
    {
      "value": "motadata",
      "label": "Motadata",
      "description": "Motadata ITSM (generic config-driven flow, JSON bodies, Bearer auth).",
      "defaults": {
        "webhook_auth_type": "Bearer",
        "webhook_body_format": "json"
      }
    }
  ],
  "fields": [
    {
      "name": "config_name",
      "label": "Configuration Name",
      "type": "input",
      "required": false,
      "default": "ITSM Webhook",
      "placeholder": "Enter name for this ITSM integration",
      "help_text": "A label to identify this integration (e.g. Jira Production)",
      "options": [],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_provider",
      "label": "ITSM Provider",
      "type": "select",
      "required": false,
      "default": "jira",
      "placeholder": "",
      "help_text": "Select the ITSM platform this integration connects to. jira: Jira Software / Jira Service Management. manage_engine: ManageEngine Service Desk. helix: BMC Helix ITSM. sapphire: SapphireIMS. motadata: Motadata.",
      "options": [
        {
          "value": "jira",
          "label": "Jira"
        },
        {
          "value": "manage_engine",
          "label": "ManageEngine"
        },
        {
          "value": "helix",
          "label": "BMC Helix"
        },
        {
          "value": "sapphire",
          "label": "SapphireIMS"
        },
        {
          "value": "motadata",
          "label": "Motadata"
        }
      ],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_method",
      "label": "Create Method",
      "type": "select",
      "required": true,
      "default": "POST",
      "placeholder": "HTTP method for ticket creation",
      "help_text": "HTTP method used when creating a new ticket",
      "options": [
        {
          "value": "POST",
          "label": "POST"
        },
        {
          "value": "PUT",
          "label": "PUT"
        },
        {
          "value": "PATCH",
          "label": "PATCH"
        }
      ],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_url",
      "label": "Create URL",
      "type": "input",
      "required": true,
      "default": "",
      "placeholder": "https://your-itsm.example.com/api/tickets",
      "help_text": "Endpoint to create a new ticket. Supports $field_name placeholders.",
      "options": [],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_auth_type",
      "label": "Auth Type",
      "type": "select",
      "required": false,
      "default": "None",
      "placeholder": "",
      "help_text": "Authentication method. Basic: provide username:password. Bearer: provide token. ApiKey: provide the API key value. HelixJWT: BMC Helix only; provide username:password.",
      "options": [
        {
          "value": "None",
          "label": "None"
        },
        {
          "value": "Basic",
          "label": "Basic"
        },
        {
          "value": "Bearer",
          "label": "Bearer"
        },
        {
          "value": "ApiKey",
          "label": "ApiKey"
        },
        {
          "value": "HelixJWT",
          "label": "HelixJWT"
        }
      ],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_auth_value",
      "label": "Auth Credentials",
      "type": "input",
      "required": false,
      "default": "",
      "placeholder": "username:password  OR  token  OR  api-key-value",
      "help_text": "Basic auth: enter username:password (auto base64-encoded). Bearer: enter the token. ApiKey: enter the API key value. Stored server-side and never returned by the API.",
      "options": [],
      "visible_when": null,
      "sensitive": true,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_auth_header",
      "label": "API Key Header Name",
      "type": "input",
      "required": false,
      "default": "X-Api-Key",
      "placeholder": "X-Api-Key",
      "help_text": "Header name to use when Auth Type is ApiKey (default: X-Api-Key). Common values: X-Authtoken, apikey.",
      "options": [],
      "visible_when": {
        "field": "webhook_auth_type",
        "value": "ApiKey",
        "values": null
      },
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_headers",
      "label": "Additional Headers",
      "type": "long_text",
      "required": false,
      "default": "",
      "placeholder": "{\"Content-Type\": \"application/json\", \"X-Custom-Header\": \"value\"}",
      "help_text": "JSON object of additional HTTP headers (for example a custom API-key header). Stored encrypted and never returned by the API: leave blank when editing to keep the stored value. Content-Type defaults to application/json when not set here.",
      "options": [],
      "visible_when": null,
      "sensitive": true,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_body_format",
      "label": "Request Body Format",
      "type": "select",
      "required": false,
      "default": "json",
      "placeholder": "",
      "help_text": "How the payload is sent to the ITSM endpoint. json: payload template is sent as a raw JSON string body (standard for Jira, ServiceNow). form: payload template is wrapped in a form field and sent as application/x-www-form-urlencoded (required by ManageEngine Service Desk webhooks).",
      "options": [
        {
          "value": "json",
          "label": "json"
        },
        {
          "value": "form",
          "label": "form"
        }
      ],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_form_param",
      "label": "Form Parameter Name",
      "type": "input",
      "required": false,
      "default": "input_data",
      "placeholder": "input_data",
      "help_text": "Name of the form field that carries the JSON payload when Body Format is form. ManageEngine Service Desk requires 'input_data'.",
      "options": [],
      "visible_when": {
        "field": "webhook_body_format",
        "value": "form",
        "values": null
      },
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_payload",
      "label": "Create Payload",
      "type": "long_text",
      "required": true,
      "default": "{\"summary\": \"$case_name\", \"description\": \"$signal_name\", \"priority\": \"$severity\"}",
      "placeholder": "{\"summary\": \"$case_name\", \"priority\": \"$severity\"}",
      "help_text": "JSON payload template for ticket creation. Use $field_name for DNIF case fields.",
      "options": [],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_ticket_id_field",
      "label": "Ticket ID Field",
      "type": "input",
      "required": true,
      "default": "key",
      "placeholder": "key",
      "help_text": "Dot-path to the ticket ID in the create response (e.g. 'key' for Jira, 'result.sys_id' for ServiceNow).",
      "options": [],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_update_url",
      "label": "Update URL",
      "type": "input",
      "required": false,
      "default": "",
      "placeholder": "https://your-itsm.example.com/api/tickets/$ticket_id",
      "help_text": "Endpoint to update an existing ticket. Use $ticket_id for the stored ticket ID.",
      "options": [],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_update_method",
      "label": "Update Method",
      "type": "select",
      "required": false,
      "default": "PUT",
      "placeholder": "HTTP method for ticket update",
      "help_text": "HTTP method used when updating an existing ticket",
      "options": [
        {
          "value": "PUT",
          "label": "PUT"
        },
        {
          "value": "PATCH",
          "label": "PATCH"
        },
        {
          "value": "POST",
          "label": "POST"
        }
      ],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_update_payload",
      "label": "Update Payload",
      "type": "long_text",
      "required": false,
      "default": "",
      "placeholder": "{\"summary\": \"$case_name\", \"status\": \"$status\"}",
      "help_text": "JSON payload template for ticket updates. Supports $field_name and $ticket_id placeholders.",
      "options": [],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "field_mappings_status",
      "label": "Status Mapping",
      "type": "key_value_mapper",
      "required": false,
      "default": "",
      "placeholder": "",
      "help_text": "Map each of your ITSM statuses to a DNIF case status. Add as many rows as your ITSM system has; multiple ITSM statuses can map to the same DNIF status. These mappings populate $itsm_status in your payload templates and translate incoming ITSM status updates back to DNIF.",
      "options": [],
      "visible_when": {
        "field": "webhook_provider",
        "value": null,
        "values": [
          "jira",
          "manage_engine",
          "motadata"
        ]
      },
      "sensitive": false,
      "default_display": true,
      "widget_config": {
        "key_label": "Your ITSM status",
        "value_label": "Maps to DNIF status",
        "value_options": [
          {
            "label": "Open",
            "value": "OPEN"
          },
          {
            "label": "In Progress",
            "value": "IN-PROGRESS"
          },
          {
            "label": "On Hold",
            "value": "ON-HOLD"
          },
          {
            "label": "Closed",
            "value": "CLOSED"
          }
        ]
      }
    },
    {
      "name": "webhook_notes_url",
      "label": "Notes URL",
      "type": "input",
      "required": false,
      "default": "",
      "placeholder": "https://your-itsm.example.com/api/tickets/$ticket_id/comments",
      "help_text": "Endpoint for posting case notes as comments. Use $ticket_id for the ticket ID.",
      "options": [],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_notes_method",
      "label": "Notes Method",
      "type": "select",
      "required": false,
      "default": "POST",
      "placeholder": "HTTP method for posting notes",
      "help_text": "HTTP method used when posting a comment/note",
      "options": [
        {
          "value": "POST",
          "label": "POST"
        },
        {
          "value": "PUT",
          "label": "PUT"
        },
        {
          "value": "PATCH",
          "label": "PATCH"
        }
      ],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "webhook_notes_payload",
      "label": "Notes Payload",
      "type": "long_text",
      "required": false,
      "default": "{\"body\": \"$note_text\"}",
      "placeholder": "{\"body\": \"$note_text\"}",
      "help_text": "JSON payload template per note. Supports $note_text, $author, $cnam_time, and any case field.",
      "options": [],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    },
    {
      "name": "config_id",
      "label": "Integration Config ID",
      "type": "readonly",
      "required": false,
      "default": "",
      "placeholder": "",
      "help_text": "Copy this ID into the token subject when creating a DNIF API token for inbound ITSM callbacks.",
      "options": [],
      "visible_when": null,
      "sensitive": false,
      "default_display": true,
      "widget_config": null
    }
  ]
}
